import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

import useCodesWalkthrough from "../hooks/useCodesWalkthrough";
import useTeamCodesAutoScroll from "../hooks/useTeamCodesAutoScroll";
import useTeamCodes from "../hooks/useTeamCodes";
import { getTeamCode, getTeamInitials } from "../data/teamCodes";

import defaultLogo from "../assets/login-logo.svg";
import stepTwoImage from "../assets/team-codes-step-2.png";
import stepThreeImage from "../assets/team-codes-step-3.png";

/*
  Encodes the branded Rebrandly short link, which forwards to
  americanyouth.com/app — a landing page offering both the App Store
  and Google Play. The ?rb.src=QR parameter tags scans from this
  dashboard so they can be told apart in Rebrandly's analytics.
*/
const APP_DOWNLOAD_URL = process.env.REACT_APP_APP_DOWNLOAD_URL;

const WALKTHROUGH_STEPS = [
  {
    heading: "Step 1",
    copy: "Download the BILT app and create your account.",
    image: null,
    imageAlt: null,
  },
  {
    heading: "Step 2",
    copy: "When prompted, enter the registration code assigned to your team.",
    image: stepTwoImage,
    imageAlt: "Enter the registration code in the BILT app",
  },
  {
    heading: "Step 3",
    copy: "Invite family and friends by text, email or share link so they can support your campaign.",
    image: stepThreeImage,
    imageAlt: "Create and send an invitation in the BILT app",
  },
];

const TeamCodeCards = ({ teamCodes }) =>
  teamCodes.map((team) => (
    <div className="codes-sidebar-card" key={team.id}>
      <span className="codes-sidebar-card__icon">{team.initials}</span>

      <span className="codes-sidebar-card__copy">
        <b>{team.name}</b>
        <small>
          {team.memberCount} {team.memberCount === 1 ? "Member" : "Members"}
        </small>
      </span>

      <strong className="codes-sidebar-card__code">{team.code}</strong>
    </div>
  ));

const TeamCodesModal = ({ isOpen, onClose, campaign, campaignId, teams }) => {
  const viewportRef = useRef(null);
  const duplicateRef = useRef(null);
  const originalRef = useRef(null);

  /*
    The list is rendered twice so the scroll can wrap without a seam.
    That second copy is only wanted when the teams actually overflow —
    otherwise both copies fit on screen and the teams look duplicated.
  */
  const [needsLoop, setNeedsLoop] = useState(false);

  const { teams: fetchedTeams } = useTeamCodes(campaignId, isOpen);

  /*
    Codes come from /teams; live member counts come from the dashboard
    payload already on screen. Match on id and prefer the dashboard's
    count so the card agrees with the leaderboard behind the modal.
  */
  const teamCodes = useMemo(() => {
    const source = fetchedTeams || teams;

    const countsById = teams.reduce((acc, team) => {
      acc[team.id] = team.numMembers;
      return acc;
    }, {});

    return source.map((team) => ({
      id: team.id,
      name: team.name,
      initials: getTeamInitials(team.name),
      code: getTeamCode(team),
      memberCount: countsById[team.id] ?? team.numMembers ?? 0,
    }));
  }, [fetchedTeams, teams]);

  const { activeIndex, leavingIndex, registerSlide } = useCodesWalkthrough(
    WALKTHROUGH_STEPS.length,
    isOpen,
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      setNeedsLoop(false);
      return undefined;
    }

    const measure = () => {
      const viewport = viewportRef.current;
      const original = originalRef.current;
      if (!viewport || !original) return;

      setNeedsLoop(original.scrollHeight > viewport.clientHeight + 1);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isOpen, teamCodes]);

  useTeamCodesAutoScroll(viewportRef, duplicateRef, isOpen && needsLoop);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const slideClassName = (index) => {
    const classes = [
      "codes-walkthrough__slide",
      `codes-walkthrough__slide--step-${index + 1}`,
    ];

    if (index === activeIndex) classes.push("is-active");
    if (index === leavingIndex) classes.push("is-leaving");

    return classes.join(" ");
  };

  return (
    <div
      id="teamModal"
      className="team-modal"
      data-current-view="team-codes"
      style={{ display: "block" }}
    >
      <div className="team-modal-content flex">
        <span
          className="team-modal-close"
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Close team codes"
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onClose();
          }}
        >
          <i className="fas fa-times"></i>
        </span>

        <div id="teammodalBody">
          <div className="team-layout codes-layout flex">
            <div className="team-layout-header flex">
              <div className="team-layout-header-left flex">
                <div className="team-layout-header-left-logo flex codes-campaign-logo">
                  <img
                    src={campaign.organization_logo || defaultLogo}
                    alt={campaign.name || ""}
                  />
                </div>

                <div className="team-layout-header-left-heading flex">
                  <h1>Team codes</h1>
                  <div>
                    <b>{teamCodes.length}</b>
                    <span>&nbsp;{teamCodes.length === 1 ? "Team" : "Teams"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="team-layout-content codes-layout-content">
              <div className="codes-modal-grid">
                <aside
                  className="codes-sidebar"
                  aria-label="Team registration codes"
                >
                  <div className="codes-sidebar__list" ref={viewportRef}>
                    <div className="codes-sidebar__track">
                      <div className="codes-sidebar__group" ref={originalRef}>
                        <TeamCodeCards teamCodes={teamCodes} />
                      </div>

                      {/* Only duplicated when the list actually scrolls. */}
                      {needsLoop && (
                        <div
                          className="codes-sidebar__group codes-sidebar__group--duplicate"
                          aria-hidden="true"
                          ref={duplicateRef}
                        >
                          <TeamCodeCards teamCodes={teamCodes} />
                        </div>
                      )}
                    </div>
                  </div>
                </aside>

                <section
                  className={`codes-walkthrough is-step-${activeIndex + 1}`}
                  aria-label="Registration walkthrough"
                >
                  <div className="codes-walkthrough__intro">
                    <span className="codes-walkthrough__eyebrow">
                      Getting started
                    </span>
                    <h2>Start fundraising now</h2>
                    <p>
                      Register in the app, connect to your campaign and invite
                      supporters to participate.
                    </p>
                  </div>

                  <div className="codes-walkthrough__stage">
                    {APP_DOWNLOAD_URL && (
                      <div className="codes-walkthrough__qr">
                        <QRCodeSVG
                          value={APP_DOWNLOAD_URL}
                          level="M"
                          marginSize={2}
                          role="img"
                          aria-label="QR code to download the American Youth app"
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    )}

                    <div className="codes-walkthrough__slides">
                      {WALKTHROUGH_STEPS.map((step, index) => (
                        <article
                          key={step.heading}
                          ref={registerSlide(index)}
                          className={slideClassName(index)}
                          data-walkthrough-step={index}
                          aria-hidden={index !== activeIndex}
                        >
                          {step.image && (
                            <div className="codes-walkthrough__image">
                              <img src={step.image} alt={step.imageAlt} />
                            </div>
                          )}

                          <div className="codes-walkthrough__slide-copy">
                            <h3>{step.heading}</h3>
                            <p>{step.copy}</p>
                          </div>
                        </article>
                      ))}
                    </div>

                    <div
                      className="codes-walkthrough__indicators"
                      aria-label="Walkthrough progress"
                    >
                      {WALKTHROUGH_STEPS.map((step, index) => (
                        <span
                          key={step.heading}
                          className={index === activeIndex ? "is-active" : ""}
                        />
                      ))}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamCodesModal;
